import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageLayout } from '@/components/Layout/PageLayout';
import { Button } from '@/components/Common/button';
import { ChatbotFormDialog } from '@/components/Chatbots/ChatbotFormDialog';
import { chatbotsService, Chatbot } from '@/services/chatbots';
import notify from '@/lib/notify';
import { useEffectiveUser, useIsOrganizationAdmin } from '@/stores/authStore';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  StarIcon,
  BotIcon,
  Users2Icon,
  EyeIcon,
} from 'lucide-react';

export const ChatbotsPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const editChatbotIdFromUrl = searchParams.get('edit_chatbot_id');
  const currentUser = useEffectiveUser();
  const isOrgAdmin = useIsOrganizationAdmin();

  // Owner can always manage; an org admin can manage org-shared agents.
  const canManage = useCallback(
    (chatbot: Chatbot) =>
      chatbot.user_id === currentUser?.id ||
      (chatbot.visibility === 'organization' && isOrgAdmin),
    [currentUser?.id, isOrgAdmin],
  );

  const [chatbots, setChatbots] = useState<Chatbot[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [editingChatbot, setEditingChatbot] = useState<Chatbot | null>(null);
  const [formReadOnly, setFormReadOnly] = useState(false);

  const loadChatbots = useCallback(async () => {
    try {
      const data = await chatbotsService.list();
      setChatbots(data);
    } catch (error) {
      console.error('Error loading chatbots:', error);
      notify.error(t('chatbots.page.loadError'));
    }
  }, [t]);

  const clearEditChatbotParam = useCallback(() => {
    if (!searchParams.has('edit_chatbot_id')) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('edit_chatbot_id');
    setSearchParams(nextParams);
  }, [searchParams, setSearchParams]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      await loadChatbots();
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [loadChatbots]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!editChatbotIdFromUrl || chatbots.length === 0) return;
    const chatbotToEdit = chatbots.find((chatbot) => chatbot.id === editChatbotIdFromUrl);
    if (!chatbotToEdit) return;
    if (showFormDialog && editingChatbot?.id === chatbotToEdit.id) return;
    setEditingChatbot(chatbotToEdit);
    setFormReadOnly(!canManage(chatbotToEdit));
    setShowFormDialog(true);
  }, [chatbots, editChatbotIdFromUrl, showFormDialog, editingChatbot?.id, canManage]);

  const handleOpenFormDialog = (chatbot?: Chatbot) => {
    setEditingChatbot(chatbot || null);
    setFormReadOnly(chatbot ? !canManage(chatbot) : false);
    setShowFormDialog(true);
  };

  const handleCloseFormDialog = () => {
    setShowFormDialog(false);
    setEditingChatbot(null);
    clearEditChatbotParam();
  };

  const handleSaveChatbot = async () => {
    await loadData();
    handleCloseFormDialog();
  };

  const handleSetDefault = async (id: string) => {
    try {
      await chatbotsService.setAsDefault(id);
      await loadChatbots();
      notify.success(t('chatbots.page.setDefaultSuccess'));
    } catch (error) {
      console.error('Error setting default:', error);
      notify.error(t('chatbots.page.updateError'));
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('chatbots.page.deleteConfirm'))) return;

    try {
      await chatbotsService.delete(id);
      await loadData();
      notify.success(t('chatbots.page.deleteSuccess'));
    } catch (error) {
      console.error('Error deleting chatbot:', error);
      notify.error(t('chatbots.page.deleteError'));
    }
  };

  return (
    <PageLayout
      icon={<BotIcon className="h-6 w-6" />}
      title={t('chatbots.page.title')}
      subtitle={t('chatbots.page.subtitle')}
    >
      {isLoading ? (
        <div className="text-center text-muted-foreground py-12">{t('common.loading')}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Card Nouveau Chatbot */}
          <div
            onClick={() => handleOpenFormDialog()}
            className="bg-card border-2 border-dashed border-border rounded-lg p-6 shadow-sm hover:shadow-md hover:border-primary transition-all cursor-pointer flex flex-col items-center justify-center min-h-[200px]"
          >
            <div className="flex flex-col items-center gap-3 text-muted-foreground hover:text-primary transition-colors">
              <div className="p-4 rounded-full bg-muted">
                <PlusIcon className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-semibold">{t('chatbots.page.newAgent')}</h3>
              <p className="text-sm text-center">{t('chatbots.page.newAgentDescription')}</p>
            </div>
          </div>

          {/* Existing Chatbots */}
          {chatbots.map((chatbot) => (
            <div
              key={chatbot.id}
              className="bg-card border border-border rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-lg font-semibold">{chatbot.name}</h3>
                    {chatbot.is_default && (
                      <StarIcon className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    )}
                    {chatbot.visibility === 'organization' && (
                      <span
                        className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300"
                        title={t('chatbots.page.sharedWithOrgTitle')}
                      >
                        <Users2Icon className="w-3 h-3" />
                        {t('chatbots.page.organizationBadge')}
                      </span>
                    )}
                    {!canManage(chatbot) && (
                      <span
                        className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground"
                        title={t('chatbots.page.readOnlyTitle')}
                      >
                        <EyeIcon className="w-3 h-3" />
                        {t('chatbots.page.readOnlyBadge')}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {chatbot.description || t('chatbots.page.noDescription')}
                  </p>
                </div>
              </div>

              {(chatbot.theme_filters?.length || chatbot.folder_paths?.length) ? (
                <div className="mb-4 flex flex-wrap gap-2">
                  {chatbot.theme_filters && chatbot.theme_filters.length > 0 && (
                    <span className="text-xs px-2 py-1 rounded bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300">
                      {t('chatbots.page.themesCount', { count: chatbot.theme_filters.length })}
                    </span>
                  )}
                  {chatbot.folder_paths && chatbot.folder_paths.length > 0 && (
                    <span className="text-xs px-2 py-1 rounded bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300">
                      {t('chatbots.page.foldersCount', { count: chatbot.folder_paths.length })}
                    </span>
                  )}
                </div>
              ) : null}

              <div className="flex items-center gap-2 mt-auto">
                {/* Default is stored per agent, so only the owner can set their own default. */}
                {chatbot.user_id === currentUser?.id && !chatbot.is_default && (
                  <Button
                    onClick={() => handleSetDefault(chatbot.id)}
                    variant="outline"
                    size="sm"
                  >
                    {t('chatbots.page.setDefault')}
                  </Button>
                )}
                {canManage(chatbot) ? (
                  <>
                    <Button
                      onClick={() => handleOpenFormDialog(chatbot)}
                      variant="outline"
                      size="sm"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleDelete(chatbot.id)}
                      variant="destructive"
                      size="sm"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={() => handleOpenFormDialog(chatbot)}
                    variant="outline"
                    size="sm"
                  >
                    <EyeIcon className="w-4 h-4 mr-1" />
                    {t('chatbots.page.view')}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialogs */}
      <ChatbotFormDialog
        open={showFormDialog}
        onClose={handleCloseFormDialog}
        chatbot={editingChatbot}
        onSave={handleSaveChatbot}
        readOnly={formReadOnly}
        canShare={isOrgAdmin}
      />
    </PageLayout>
  );
};
